/**
 * `useSignedAction()` — obtain a *fresh* access token for an electronic
 * signature (CLAUDE.md §9.7).
 *
 * The backend's `SignatureService` requires the token's OIDC `auth_time` to
 * be within the freshness window. A silently-renewed token carries the
 * *original* auth_time, so the normal `useAccessToken()` will be rejected
 * with `401 signature_reauth_required`. This hook forces an interactive
 * `prompt: "login"` popup — Entra re-authenticates (and, with Conditional
 * Access, re-challenges MFA) — and returns the resulting token, which is what
 * the signing call must use.
 */

import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuthConfig } from "@/features/auth/useAuthConfig";

export function useSignedAction(): () => Promise<string> {
  const { instance, accounts } = useMsal();
  const config = useAuthConfig();

  return useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error("Not signed in.");
    const result = await instance.acquireTokenPopup({
      account,
      scopes: config.scopes,
      // Forces a real re-authentication rather than a cached session — this
      // is the second credential the signature demands.
      prompt: "login",
    });
    return result.accessToken;
  }, [instance, accounts, config.scopes]);
}
